import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FileText, Shield, AlertCircle, Percent } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-warm">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          ← Back
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold">Terms of Service</h1>
        </div>

        <Card className="p-8 shadow-warm border-border/50 space-y-6">
          <section>
            <p className="text-muted-foreground mb-4">Last updated: {new Date().toLocaleDateString()}</p>
            <p>Welcome to imata! By using our service, you agree to these terms. Please read them carefully.</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              User Responsibilities
            </h2>
            <p className="text-muted-foreground">As a imata user, you agree to:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Be respectful and kind in all interactions</li>
              <li>Provide accurate information during registration</li>
              <li>Keep your account credentials secure</li>
              <li>Not use the platform for any illegal activities</li>
              <li>Not harass, abuse, or harm other users</li>
              <li>Not create fake accounts or impersonate others</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">Gifting Conduct</h2>
            <p className="text-muted-foreground">imata is designed to spread joy and kindness. You agree to:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Send gifts with genuine intent to spread happiness</li>
              <li>Include appropriate, respectful messages</li>
              <li>Not use gifts for solicitation or spam</li>
              <li>Not request refunds for completed gifts</li>
              <li>Understand that gifts are voluntary and non-refundable</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">Content Guidelines</h2>
            <p className="text-muted-foreground">Messages and content must not contain:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Hate speech, discrimination, or harassment</li>
              <li>Explicit or inappropriate content</li>
              <li>Spam or promotional material</li>
              <li>False or misleading information</li>
              <li>Personal information of others</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">Payment Terms</h2>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>All payments are processed securely through third-party providers</li>
              <li>Gifts are final and non-refundable once sent</li>
              <li>Processing fees apply to transactions (see Platform Fees below)</li>
              <li>We reserve the right to refuse or cancel transactions</li>
              <li>You are responsible for any taxes on gifts sent or received</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Percent className="w-5 h-5 text-primary" />
              Platform Fees
            </h2>
            <p className="text-muted-foreground">
              imata charges a platform fee on all gifts to cover payment processing, platform maintenance, and customer support.
            </p>
            
            <div className="space-y-2">
              <h3 className="font-medium">Fee Structure</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li><strong>Rate:</strong> 10% of the gift amount</li>
                <li><strong>Minimum fee:</strong> £0.50</li>
                <li><strong>Maximum fee:</strong> £3.50</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h3 className="font-medium">Who Pays the Fee</h3>
              <p className="text-muted-foreground">
                The sender pays the platform fee in addition to the gift amount. Recipients receive 100% of the gift amount—the fee is never deducted from their gift.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-medium">Fee Examples</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-border rounded-lg">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Gift Amount</th>
                      <th className="px-3 py-2 text-left font-medium">Platform Fee</th>
                      <th className="px-3 py-2 text-left font-medium">Total Charged</th>
                      <th className="px-3 py-2 text-left font-medium">Recipient Gets</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-t border-border">
                      <td className="px-3 py-2">£5</td>
                      <td className="px-3 py-2">£0.50 (min)</td>
                      <td className="px-3 py-2">£5.50</td>
                      <td className="px-3 py-2">£5.00</td>
                    </tr>
                    <tr className="border-t border-border">
                      <td className="px-3 py-2">£10</td>
                      <td className="px-3 py-2">£1.00</td>
                      <td className="px-3 py-2">£11.00</td>
                      <td className="px-3 py-2">£10.00</td>
                    </tr>
                    <tr className="border-t border-border">
                      <td className="px-3 py-2">£25</td>
                      <td className="px-3 py-2">£2.50</td>
                      <td className="px-3 py-2">£27.50</td>
                      <td className="px-3 py-2">£25.00</td>
                    </tr>
                    <tr className="border-t border-border">
                      <td className="px-3 py-2">£50</td>
                      <td className="px-3 py-2">£3.50 (max)</td>
                      <td className="px-3 py-2">£53.50</td>
                      <td className="px-3 py-2">£50.00</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="font-medium">Additional Information</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Fees are calculated and displayed before you confirm payment</li>
                <li>All fees are non-refundable once a gift is successfully sent</li>
                <li>Fee structure may be updated with prior notice to users</li>
              </ul>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Account Termination
            </h2>
            <p className="text-muted-foreground">We reserve the right to suspend or terminate accounts that:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Violate these Terms of Service</li>
              <li>Engage in abusive or harassing behavior</li>
              <li>Attempt to defraud the platform or other users</li>
              <li>Use the service for illegal purposes</li>
              <li>Create multiple accounts to abuse the system</li>
            </ul>
            <p className="text-muted-foreground mt-2">
              Terminated accounts may not be eligible for refunds of pending gifts.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">Moderation</h2>
            <p className="text-muted-foreground">To maintain a safe and positive community:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>We review reported content and behavior</li>
              <li>Inappropriate content may be removed without notice</li>
              <li>Repeat violations may result in permanent bans</li>
              <li>Users can report violations through the app</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">Intellectual Property</h2>
            <p className="text-muted-foreground">imata and its content are protected by intellectual property laws:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>The imata name, logo, and branding are our trademarks</li>
              <li>You may not copy or reproduce our app or content</li>
              <li>User-generated content remains yours, but you grant us license to display it</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">Liability Limitations</h2>
            <p className="text-muted-foreground">imata is provided "as is" without warranties:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>We are not responsible for user interactions or gifts</li>
              <li>Technical issues may occur and interrupt service</li>
              <li>We are not liable for indirect or consequential damages</li>
              <li>Maximum liability is limited to amounts you've paid us</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">Changes to Terms</h2>
            <p className="text-muted-foreground">
              We may update these terms occasionally. Continued use of imata after changes constitutes acceptance of
              new terms. We'll notify users of significant changes.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">Contact & Disputes</h2>
            <p className="text-muted-foreground">For questions or concerns about these terms:</p>
            <p className="text-muted-foreground">
              Email:{" "}
              <a href="mailto:support@peespees.com" className="text-primary hover:underline">
                support@peespees.com
              </a>
            </p>
          </section>

          <section className="pt-4 border-t border-border space-y-3">
            <p className="text-sm text-muted-foreground">
              By using imata, you acknowledge that you have read, understood, and agree to be bound by these Terms of
              Service.
            </p>
            <p className="text-sm text-muted-foreground">
              Related policies:{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              {" | "}
              <Link to="/safety" className="text-primary hover:underline">
                Safety Policy
              </Link>
            </p>
          </section>
        </Card>
      </div>
    </div>
  );
};

export default Terms;
